// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { apiMrqdashboardGetmrqdashboardSystemSerialNumberSystemMaterialNumberGet } from '../../../../api/remote-api/.portal.generated/fn/mrq-dashboard/api-mrqdashboard-getmrqdashboard-system-serial-number-system-material-number-get';
import { Mrq10KSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-10-k-system-model';
import { Mrq4KSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-4-k-system-model';
import { MrqDryCoolSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-dry-cool-system-model';
import { MapApiService } from '../../../shared/services/map-api.service';
import { MrqReportData } from '../../interfaces/mrq.interfaces';

type MrqSystemModel =
  | Mrq4KSystemModel
  | Mrq10KSystemModel
  | MrqDryCoolSystemModel;

// Type alias for MRQ value type
type MrqValueType = string | number | null;

@Injectable({
  providedIn: 'root',
})
export class MrqReportService {
  /**
   * Generates column definitions from MRQ report data
   */
  getColumnsFromReportData(
    reportData: MrqReportData[],
  ): Array<{ id: string; name: string }> {
    if (reportData && reportData.length > 0) {
      const firstRow = reportData[0];
      const valueKeys = firstRow.values ? Object.keys(firstRow.values) : [];
      return [
        { id: 'date', name: 'Date' },
        ...valueKeys.map((key) => ({ id: key, name: key })),
      ];
    }
    return [{ id: 'date', name: 'Date' }];
  }

  /**
   * Normalizes backend MRQ model arrays to UI-friendly MrqReportData[]
   */
  mapToMrqReportData(
    arr: MrqSystemModel[],
    magnetType: string,
  ): MrqReportData[] {
    return (arr ?? []).map((item) => ({
      date: item.date ?? '',
      isWeekend: false,
      magnetType,
      values: this.normalizeMrqValues(item),
    }));
  }

  private normalizeMrqValues(
    item: MrqSystemModel,
  ): Record<string, MrqValueType> {
    const values: Record<string, MrqValueType> = {};
    Object.keys(item)
      .filter((key) => key !== 'date')
      .forEach((key) => {
        const rawValue = item[key as keyof typeof item];
        values[key] = this.extractMrqValue(rawValue);
      });
    return values;
  }

  private extractMrqValue(val: unknown): MrqValueType {
    // If the value is an object with a 'value' property, extract it
    if (val !== null && typeof val === 'object' && 'value' in val) {
      const valueProp = (val as { value: string | number | null }).value;
      return valueProp ?? null;
    }

    // If the value is a primitive (string, number, or null), return as is
    if (typeof val === 'string' || typeof val === 'number' || val === null) {
      return val;
    }

    // For all other cases (e.g., undefined, objects without 'value'), return null
    return null;
  }
  // Type alias for MRQ value type
  protected mapApiservice = inject(MapApiService);
  private _isLoading = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._isLoading.asObservable();
  private http = inject(HttpClient);
  getMrqDashboard = this.mapApiservice.mapEndpoint(
    apiMrqdashboardGetmrqdashboardSystemSerialNumberSystemMaterialNumberGet,
  );
  /**
   * Gets MRQ report data for a specific system and magnet type
   * Fetches from backend API
   */
  getMrqReportData(
    systemSerialNumber: string,
    systemMaterialNumber: number,
  ): Observable<{
    magnet4k: Mrq4KSystemModel[];
    magnet10k: Mrq10KSystemModel[];
    magnetDryCool: MrqDryCoolSystemModel[];
  }> {
    this._isLoading.next(true);
    return this.fetchMrqDashboard(
      systemSerialNumber,
      systemMaterialNumber,
    ).pipe(
      map((response) => this.mapMrqDashboardResponse(response)),
      tap(() => this._isLoading.next(false)),
      catchError(() => of({ magnet4k: [], magnet10k: [], magnetDryCool: [] })),
    );
  }
  /**
   * Calls the backend API for MRQ dashboard data
   */
  private fetchMrqDashboard(
    systemSerialNumber: string,
    systemMaterialNumber: number,
  ): Observable<unknown> {
    return this.getMrqDashboard.request({
      systemSerialNumber,
      systemMaterialNumber,
    });
  }
  /**
   * Maps the backend response to the expected MRQ report data structure
   */
  private mapMrqDashboardResponse(response: unknown): {
    magnet4k: Mrq4KSystemModel[];
    magnet10k: Mrq10KSystemModel[];
    magnetDryCool: MrqDryCoolSystemModel[];
  } {
    if (
      typeof response === 'object' &&
      response !== null &&
      'magnet4k' in response &&
      'magnet10k' in response &&
      'magnetDryCool' in response
    ) {
      const typedResponse = response as {
        magnet4k: Mrq4KSystemModel[];
        magnet10k: Mrq10KSystemModel[];
        magnetDryCool: MrqDryCoolSystemModel[];
      };
      return {
        magnet4k: typedResponse.magnet4k ?? [],
        magnet10k: typedResponse.magnet10k ?? [],
        magnetDryCool: typedResponse.magnetDryCool ?? [],
      };
    }
    return { magnet4k: [], magnet10k: [], magnetDryCool: [] };
  }

  /**
   * Maps backend API response to MrqReportData[]
   */
  // Removed custom mapping, now using generated types directly

  /**
   * Returns the most recent date data from a list of MrqReportData
   */
  getMostRecentDateData(data: MrqReportData[]): MrqReportData | null {
    if (!data || data.length === 0) return null;
    const sortedData = [...data].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    return sortedData[0];
  }

  /**
   * Returns columns for mobile view (excluding the date column)
   */
  getMobileColumns(
    columns: Array<{ id: string; name: string; tooltip?: string }>,
  ): Array<{ id: string; name: string; tooltip?: string }> {
    return columns.filter((col) => col.id !== 'date');
  }
}
